import React from 'react';

const useSelection = ({ rowKey, pageData = [] }) => {
  const getRowKey = React.useMemo(() => {
    if (typeof rowKey === 'function') {
      return rowKey;
    }

    return (record) => record[rowKey];
  }, [rowKey]);

  const [selectedKeys, setSelectedKeys] = React.useState([]);
  const [lastSelectedKey, setLastSelectedKey] = React.useState(null);

  const selectedKeySet = React.useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const selectOptions = React.useMemo(
    () => [
      {
        key: 'all',
        text: 'SELECT ALL',
        onSelect() {
          setSelectedKeys(pageData.map(getRowKey));
        },
      },
      {
        key: 'invert',
        text: 'INVERT',
        onSelect() {
          const keySet = new Set(selectedKeySet);

          pageData.forEach((record) => {
            const key = getRowKey(record);

            if (keySet.has(key)) {
              keySet.delete(key);
            } else {
              keySet.add(key);
            }
          });

          setSelectedKeys(Array.from(keySet));
        },
      },
    ],
    [setSelectedKeys, pageData, getRowKey, selectedKeySet],
  );

  const recordKeys = React.useMemo(() => pageData.map(getRowKey), [pageData, getRowKey]);
  const checkedCurrentAll = recordKeys.every((key) => selectedKeySet.has(key));
  const checkedCurrentSome = recordKeys.some((key) => selectedKeySet.has(key));

  // TODO: memoize?
  const keySet = new Set(selectedKeySet);

  const onSelectAllChange = () => {
    if (checkedCurrentAll) {
      recordKeys.forEach((key) => {
        keySet.delete(key);
      });
    } else {
      recordKeys.forEach((key) => {
        keySet.add(key);
      });
    }

    setSelectedKeys(Array.from(keySet));
  };

  const onSingleSelection = ({ record, nativeEvent }) => {
    const key = getRowKey(record);
    const checked = keySet.has(key);
    const { shiftKey } = nativeEvent;

    let startIndex = -1;
    let endIndex = -1;

    // Get range of this
    if (shiftKey) {
      const pointKeys = new Set([lastSelectedKey, key]);

      recordKeys.some((recordKey, recordIndex) => {
        if (pointKeys.has(recordKey)) {
          if (startIndex === -1) {
            startIndex = recordIndex;
          } else {
            endIndex = recordIndex;
            return true;
          }
        }

        return false;
      });
    }

    if (endIndex !== -1 && startIndex !== endIndex) {
      // Batch update selections
      const rangeKeys = recordKeys.slice(startIndex, endIndex + 1);

      if (checked) {
        rangeKeys.forEach((recordKey) => {
          if (keySet.has(recordKey)) {
            keySet.delete(recordKey);
          }
        });
      } else {
        rangeKeys.forEach((recordKey) => {
          if (!keySet.has(recordKey)) {
            keySet.add(recordKey);
          }
        });
      }
    } else if (checked) {
      keySet.delete(key);
    } else {
      keySet.add(key);
    }

    setSelectedKeys(Array.from(keySet));
    setLastSelectedKey(key);
  };

  const isSelected = (record) => keySet.has(getRowKey(record));

  const clearSelectedKeys = () => {
    setSelectedKeys([]);
    setLastSelectedKey(null);
  };

  return {
    selectedKeys,
    isSelected,
    clearSelectedKeys,

    selectOptions,
    checkedCurrentAll,
    checkedCurrentSome,

    onSingleSelection,
    onSelectAllChange,
  };
};

export default useSelection;
